import { EscortProfile, EscortRate } from '@/types';
import { supabase } from '@/integrations/supabase/client';

/**
 * Transforms the raw profile data from Supabase into our application's EscortProfile type
 */
export const transformProfileData = (
  profileData: any, 
  services: any[] = [], 
  rates: any[] = []
): EscortProfile => {
  const photos = Array.isArray(profileData.escort_photos) ? profileData.escort_photos : [];
  let mainPhoto = '';
  
  if (profileData.main_photo && profileData.main_photo.photo_url) {
    mainPhoto = profileData.main_photo.photo_url;
  } else {
    const mainPhotoObj = photos.find(p => p.is_main);
    if (mainPhotoObj) {
      mainPhoto = mainPhotoObj.photo_url;
    }
  }
  
  const galleryPhotos = photos.filter(p => !p.is_main && !p.is_private).map(p => p.photo_url) || [];
  const privatePhotos = photos.filter(p => p.is_private).map(p => p.photo_url) || [];
  
  const transformedServices = services.map(service => ({
    name: service.name,
    description: service.description || undefined,
    visibility: service.visibility as 'public' | 'teaser' | 'regular' | 'vip',
    status: service.status as 'included' | 'extra' | 'refused',
    price: service.price || undefined
  }));
  
  console.log('Transforming rates data:', rates);
  
  const transformedRates: EscortRate[] = rates.map(rate => ({
    id: rate.id || `rate-${Math.random().toString(36).substr(2, 9)}`,
    category: rate.category || 'incall',
    description: rate.description,
    price: rate.price,
    currency: rate.currency || 'EUR',
    duration: rate.duration || undefined,
    visibility: rate.visibility as 'public' | 'teaser' | 'regular' | 'vip',
    incall: true, // Default to true for incall
    outcall: rate.description?.toLowerCase().includes('outcall') || false // Check if description mentions outcall
  }));

  console.log('Transformed rates:', transformedRates);

  // Construct the full EscortProfile object
  const transformedProfile: EscortProfile = {
    id: profileData.id,
    userId: profileData.user_id,
    displayName: profileData.display_name,
    age: profileData.age,
    bio: {
      short: profileData.bio_short || '',
      full: profileData.bio_full || ''
    },
    photos: {
      main: mainPhoto,
      gallery: galleryPhotos,
      private: privatePhotos
    },
    location: {
      city: profileData.city || '',
      country: profileData.country || '',
      latitude: profileData.latitude,
      longitude: profileData.longitude
    },
    attributes: {
      gender: profileData.gender,
      height: profileData.height,
      weight: profileData.weight,
      bodyType: profileData.body_type,
      hairColor: profileData.hair_color,
      eyeColor: profileData.eye_color,
      ethnicity: profileData.ethnicity,
      nationality: profileData.nationality,
      measurements: profileData.measurements,
      cupSize: profileData.cup_size,
      intimateArea: profileData.intimate_area,
      tattoos: profileData.has_tattoos,
      piercings: profileData.has_piercings,
      smoking: profileData.smoking,
      drinking: profileData.drinking,
      sexualOrientation: profileData.sexual_orientation,
      languages: []
    },
    services: transformedServices,
    rates: transformedRates,
    isVerified: profileData.is_verified,
    isOnline: profileData.is_online,
    isTouring: profileData.is_touring,
    tourDates: [],
    ai_chat_enabled: profileData.ai_chat_enabled || false,
    ai_chat_response_mode: profileData.ai_chat_response_mode || 'always',
    ai_chat_schedule: profileData.ai_chat_schedule || null
  };

  return transformedProfile;
};

/**
 * Gets the full profile image URL, handling both local and remote URLs
 */

export const getFullProfileImageUrl = (url?: string): string => {
  if (!url) return '';
  
  // Si c'est déjà une URL complète, la retourner telle quelle
  if (url.startsWith('http')) return url;
  
  // Sinon, construire l'URL complète
  return `${process.env.VITE_SUPABASE_URL}/storage/v1/object/public/${url}`;
};
